import React from 'react';
import loadable from '@loadable/component';
import { Redirect } from 'react-router-dom';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import NamedRedirect from '../components/NamedRedirect/NamedRedirect';
import { ACCOUNT_SETTINGS_PAGES } from '../constants/accountSettingsPages';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const AuthenticationPageProvider = loadable(() => import(/* webpackChunkName: "AuthenticationPageProvider" */ '../containers/AuthenticationPage/AuthenticationPageProvider'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const GuestOrderPage = loadable(() => import(/* webpackChunkName: "GuestOrderPage" */ '../containers/GuestOrderPage/GuestOrderPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const CataloguePage = loadable(() => import(/* webpackChunkName: "CataloguePage" */ '../containers/CataloguePage/CataloguePage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const ProfileDashboardPage = loadable(() => import(/* webpackChunkName: "ProfileDashboardPage" */ '../containers/ProfileDashboardPage/ProfileDashboardPage'));
const OrdersPage = loadable(() => import(/* webpackChunkName: "OrdersPage" */ '../containers/OrdersPage/OrdersPage'));
const MyRequestsPage = loadable(() => import(/* webpackChunkName: "MyRequestsPage" */ '../containers/MyRequestsPage/MyRequestsPage'));
const NewSupportRequestPage = loadable(() => import(/* webpackChunkName: "NewSupportRequestPage" */ '../containers/MyRequestsPage/NewSupportRequestPage'));
const SupportRequestDetailPage = loadable(() => import(/* webpackChunkName: "SupportRequestDetailPage" */ '../containers/MyRequestsPage/SupportRequestDetailPage'));
const ProgrammeFidelitePage = loadable(() => import(/* webpackChunkName: "ProgrammeFidelitePage" */ '../containers/ProgrammeFidelitePage/ProgrammeFidelitePage'));
const ProgrammeAffiliationPage = loadable(() => import(/* webpackChunkName: "ProgrammeAffiliationPage" */ '../containers/ProgrammeAffiliationPage/ProgrammeAffiliationPage'));
const CagnotteFidelitePage = loadable(() => import(/* webpackChunkName: "CagnotteFidelitePage" */ '../containers/CagnotteFidelitePage/CagnotteFidelitePage'));
const PaymentIntentsPage = loadable(() => import(/* webpackChunkName: "PaymentIntentsPage" */ '../containers/PaymentIntentsPage/PaymentIntentsPage'));
const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TermsOfServiceFallbackPage = loadable(() => import(/* webpackChunkName: "TermsOfServiceFallbackPage" */ '../containers/TermsOfServicePage/FallbackPage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const NoAccessPage = loadable(() => import(/* webpackChunkName: "NoAccessPage" */ '../containers/NoAccessPage/NoAccessPage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));
const BrandsPage = loadable(() => import(/* webpackChunkName: "BrandsPage" */ '../containers/BrandsPage/BrandsPage'));
const BlogPage = loadable(() => import(/* webpackChunkName: "BlogPage" */ '../containers/BlogPage/BlogPage'));
const CategoriesPage = loadable(() => import(/* webpackChunkName: "CategoriesPage" */ '../containers/CategoriesPage/CategoriesPage'));
const CompatibilityPage = loadable(() => import(/* webpackChunkName: "CompatibilityPage" */ '../containers/CompatibilityPage/CompatibilityPage'));
const AboutPage = loadable(() => import(/* webpackChunkName: "AboutPage" */ '../containers/AboutPage/AboutPage'));
const ContactPageNew = loadable(() => import(/* webpackChunkName: "ContactPageNew" */ '../containers/ContactPage/ContactPage'));
const BecomeSellerPage = loadable(() => import(/* webpackChunkName: "BecomeSellerPage" */ '../containers/BecomeSellerPage/BecomeSellerPage'));
const AccompagnementProjetDomotiquePage = loadable(() =>
  import(/* webpackChunkName: "AccompagnementProjetDomotiquePage" */ '../containers/AccompagnementProjetDomotiquePage/AccompagnementProjetDomotiquePage')
);
  // Static legal & trust pages
  const ContactPage = loadable(() => import(/* webpackChunkName: "ContactPage" */ '../containers/LegalPages/ContactPage'));
  const FAQPage = loadable(() => import(/* webpackChunkName: "FAQPage" */ '../containers/LegalPages/FAQPage'));
  const TermsOfServicePageLegal = loadable(() => import(/* webpackChunkName: "TermsOfServicePageLegal" */ '../containers/LegalPages/TermsOfServicePage'));
  const PrivacyPolicyPageLegal = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPageLegal" */ '../containers/LegalPages/PrivacyPolicyPage'));
  const LegalNoticePageLegal = loadable(() => import(/* webpackChunkName: "LegalNoticePageLegal" */ '../containers/LegalPages/LegalNoticePage'));

// Article pages
const ArticlesPage = loadable(() => import(/* webpackChunkName: "ArticlesPage" */ '../containers/ArticlesPage/ArticlesPage'));
const WiFiPage = loadable(() => import(/* webpackChunkName: "WiFiPage" */ '../containers/ArticlesPage/WiFiPage'));
const SmartHomeProtocolsPage = loadable(() => import(/* webpackChunkName: "SmartHomeProtocolsPage" */ '../containers/ArticlesPage/SmartHomeProtocolsPage'));
const VideoDoorbellPage = loadable(() => import(/* webpackChunkName: "VideoDoorbellPage" */ '../containers/ArticlesPage/VideoDoorbellPage'));

// New article pages
const SmartLockPage = loadable(() => import(/* webpackChunkName: "SmartLockPage" */ '../containers/ArticlesPage/SmartLockPage'));
const ThermostaticHeadsPage = loadable(() => import(/* webpackChunkName: "ThermostaticHeadsPage" */ '../containers/ArticlesPage/ThermostaticHeadsPage'));
const SmartHeatingDevicesPage = loadable(() => import(/* webpackChunkName: "SmartHeatingDevicesPage" */ '../containers/ArticlesPage/SmartHeatingDevicesPage'));
const BabyRoomThermostatPage = loadable(() => import(/* webpackChunkName: "BabyRoomThermostatPage" */ '../containers/ArticlesPage/BabyRoomThermostatPage'));
const ChatSupportPage = loadable(() => import(/* webpackChunkName: "ChatSupportPage" */ '../containers/ChatSupportPage/ChatSupportPage'));
const CartPage = loadable(() => import(/* webpackChunkName: "CartPage" */ '../containers/CartPage/CartPage'));
const ComparePage = loadable(() => import(/* webpackChunkName: "ComparePage" */ '../containers/ComparePage/ComparePage'));

// Blog pages
const BlogListPage = loadable(() => import(/* webpackChunkName: "BlogListPage" */ '../containers/BlogListPage/BlogListPage'));
const BlogPostPage = loadable(() => import(/* webpackChunkName: "BlogPostPage" */ '../containers/BlogPostPage/BlogPostPage'));
const BundlesPage = loadable(() => import(/* webpackChunkName: "BundlesPage" */ '../containers/BundlesPage/BundlesPage'));
const BlogAdminPage = loadable(() => import(/* webpackChunkName: "BlogAdminPage" */ '../containers/BlogAdminPage/BlogAdminPage'));
const CategorySlugsAdminPage = loadable(() => import(/* webpackChunkName: "CategorySlugsAdminPage" */ '../containers/CategorySlugsAdminPage/CategorySlugsAdminPage'));
const AdminTransactionsPage = loadable(() => import(/* webpackChunkName: "AdminTransactionsPage" */ '../containers/AdminTransactionsPage/AdminTransactionsPage'));
const AdminTransactionDetailPage = loadable(() => import(/* webpackChunkName: "AdminTransactionDetailPage" */ '../containers/AdminTransactionDetailPage/AdminTransactionDetailPage'));
const AdminUsersPage = loadable(() => import(/* webpackChunkName: "AdminUsersPage" */ '../containers/AdminUsersPage/AdminUsersPage'));
const AdminUserDetailPage = loadable(() => import(/* webpackChunkName: "AdminUserDetailPage" */ '../containers/AdminUserDetailPage/AdminUserDetailPage'));
const AdminAbandonedCartsPage = loadable(() => import(/* webpackChunkName: "AdminAbandonedCartsPage" */ '../containers/AdminAbandonedCartsPage/AdminAbandonedCartsPage'));
const CategoriesAdminPage = loadable(() => import(/* webpackChunkName: "CategoriesAdminPage" */ '../containers/CategoriesAdminPage/CategoriesAdminPage'));
const BrandsAdminPage = loadable(() => import(/* webpackChunkName: "BrandsAdminPage" */ '../containers/BrandsAdminPage/BrandsAdminPage'));
const AdminLandingPage = loadable(() => import(/* webpackChunkName: "AdminLandingPage" */ '../containers/AdminLandingPage/AdminLandingPage'));
const BlogPostEditor = loadable(() => import(/* webpackChunkName: "BlogPostEditor" */ '../containers/BlogPostEditor/BlogPostEditor'));
const AdminNewsletterDashboardPage = loadable(() => import(/* webpackChunkName: "AdminNewsletterDashboardPage" */ '../containers/AdminNewsletterDashboardPage/AdminNewsletterDashboardPage'));
const AdminNewsletterSubscribersPage = loadable(() => import(/* webpackChunkName: "AdminNewsletterSubscribersPage" */ '../containers/AdminNewsletterSubscribersPage/AdminNewsletterSubscribersPage'));
const AdminNewsletterCampaignsPage = loadable(() => import(/* webpackChunkName: "AdminNewsletterCampaignsPage" */ '../containers/AdminNewsletterCampaignsPage/AdminNewsletterCampaignsPage'));
const AdminNewsletterCampaignEditPage = loadable(() => import(/* webpackChunkName: "AdminNewsletterCampaignEditPage" */ '../containers/AdminNewsletterCampaignEditPage/AdminNewsletterCampaignEditPage'));
const AdminNewsletterCampaignDetailPage = loadable(() => import(/* webpackChunkName: "AdminNewsletterCampaignDetailPage" */ '../containers/AdminNewsletterCampaignDetailPage/AdminNewsletterCampaignDetailPage'));
const AdminNewsletterSegmentsPage = loadable(() => import(/* webpackChunkName: "AdminNewsletterSegmentsPage" */ '../containers/AdminNewsletterSegmentsPage/AdminNewsletterSegmentsPage'));
const NewsletterUnsubscribePage = loadable(() => import(/* webpackChunkName: "NewsletterUnsubscribePage" */ '../containers/NewsletterUnsubscribePage/NewsletterUnsubscribePage'));

export { ACCOUNT_SETTINGS_PAGES };

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;
const isDealsQueryActive = search => {
  const deals = new URLSearchParams(search || '').get('deals');
  return deals === 'true' || deals === '1';
};
const withDealsQuery = search => {
  const queryParams = new URLSearchParams(search || '');
  queryParams.set('deals', 'true');
  return `?${queryParams.toString()}`;
};
const withoutDealsQuery = search => {
  const queryParams = new URLSearchParams(search || '');
  queryParams.delete('deals');
  const query = queryParams.toString();
  return query ? `?${query}` : '';
};

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig, accessControlConfig) => {
  const SearchPage = layoutConfig.searchPage?.variantType === 'map'
    ? SearchPageWithMap
    : SearchPageWithGrid;
  const ListingPage = layoutConfig.listingPage?.variantType === 'carousel'
    ? ListingPageCarousel
    : ListingPageCoverPhoto;

  const isPrivateMarketplace = accessControlConfig?.marketplace?.private === true;
  const authForPrivateMarketplace = isPrivateMarketplace ? { auth: true } : {};

  return [
    {
      path: '/sales',
      name: 'SalesPage',
      auth: true,
      authPage: 'LoginPage',
      component: require('../containers/SalesPage').default,
      loadData: require('../containers/pageDataLoadingAPI').default().SalesPage.loadData,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'provider',
    },
    {
      path: '/admin/content',
      name: 'BlogAdminPage',
      auth: true,
      authPage: 'LoginPage',
      component: BlogAdminPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/category-slugs',
      name: 'CategorySlugsAdminPage',
      auth: true,
      authPage: 'LoginPage',
      component: CategorySlugsAdminPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/transactions',
      name: 'AdminTransactionsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminTransactionsPage,
      loadData: pageDataLoadingAPI.AdminTransactionsPage.loadData,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/transactions/:id',
      name: 'AdminTransactionDetailPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminTransactionDetailPage,
      loadData: pageDataLoadingAPI.AdminTransactionDetailPage.loadData,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/users',
      name: 'AdminUsersPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminUsersPage,
      loadData: pageDataLoadingAPI.AdminUsersPage.loadData,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/users/:id',
      name: 'AdminUserDetailPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminUserDetailPage,
      loadData: pageDataLoadingAPI.AdminUserDetailPage.loadData,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/abandoned-carts',
      name: 'AdminAbandonedCartsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminAbandonedCartsPage,
      loadData: pageDataLoadingAPI.AdminAbandonedCartsPage.loadData,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/categories',
      name: 'CategoriesAdminPage',
      auth: true,
      authPage: 'LoginPage',
      component: CategoriesAdminPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/brands',
      name: 'BrandsAdminPage',
      auth: true,
      authPage: 'LoginPage',
      component: BrandsAdminPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/landing',
      name: 'AdminLandingPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminLandingPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/newsletter',
      name: 'AdminNewsletterDashboardPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminNewsletterDashboardPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/newsletter/subscribers',
      name: 'AdminNewsletterSubscribersPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminNewsletterSubscribersPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/newsletter/segments',
      name: 'AdminNewsletterSegmentsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminNewsletterSegmentsPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/newsletter/campaigns',
      name: 'AdminNewsletterCampaignsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminNewsletterCampaignsPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/newsletter/campaigns/new',
      name: 'AdminNewsletterCampaignNewPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminNewsletterCampaignEditPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/newsletter/campaigns/:id/edit',
      name: 'AdminNewsletterCampaignEditPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminNewsletterCampaignEditPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/newsletter/campaigns/:id',
      name: 'AdminNewsletterCampaignDetailPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminNewsletterCampaignDetailPage,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/newsletter/unsubscribe',
      name: 'NewsletterUnsubscribePage',
      component: NewsletterUnsubscribePage,
    },
    // Redirection pour compatibilité
    {
      path: '/admin/blog',
      name: 'BlogAdminPageRedirect',
      component: () => <NamedRedirect name="BlogAdminPage" />,
    },
    {
      path: '/admin/blog/new',
      name: 'BlogPostEditorNew',
      auth: true,
      authPage: 'LoginPage',
      component: BlogPostEditor,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/admin/blog/edit/:slug',
      name: 'BlogPostEditorEdit',
      auth: true,
      authPage: 'LoginPage',
      component: BlogPostEditor,
      shouldRender: (config, { currentUser }) =>
        currentUser?.attributes?.profile?.publicData?.userType === 'admin',
    },
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/brands',
      name: 'BrandsPage',
      component: BrandsPage,
    },
    {
      path: '/categories',
      name: 'CategoriesPage',
      component: CategoriesPage,
    },
    {
      path: '/compatibility',
      name: 'CompatibilityPage',
      component: CompatibilityPage,
    },
    {
      path: '/blog',
      name: 'BlogListPage',
      component: BlogListPage,
      loadData: pageDataLoadingAPI.BlogListPage.loadData,
    },
    {
      path: '/blog/:slug',
      name: 'BlogPostPage',
      component: BlogPostPage,
      loadData: pageDataLoadingAPI.BlogPostPage.loadData,
    },
    {
      path: '/blog-old',
      name: 'BlogPage',
      component: BlogPage,
    },
    {
      path: '/about',
      name: 'AboutPage',
      component: AboutPage,
    },
    {
      path: '/support',
      name: 'ChatSupportPage',
      component: ChatSupportPage,
    },
    {
      path: '/cart',
      name: 'CartPage',
      auth: false,
      component: CartPage,
    },
    {
      path: '/landing-page',
      name: 'LandingPageAlt',
      component: LandingPage,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      // Handles legacy /p/* links that may contain nested page IDs.
      path: '/p/:pageId+',
      name: 'CMSPageNested',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    // Static legal & trust pages
    { path: '/contact', name: 'ContactPage', component: ContactPageNew },
    { path: '/devenir-vendeur', name: 'BecomeSellerPage', component: BecomeSellerPage },
    { path: '/faq', name: 'FAQPage', component: FAQPage },
    {
      path: '/accompagnement-projet-domotique',
      name: 'AccompagnementProjetDomotiquePage',
      component: AccompagnementProjetDomotiquePage,
    },
    {
      path: '/packs',
      name: 'PacksPage',
      component: BundlesPage,
      loadData: pageDataLoadingAPI.BundlesPage.loadData,
    },
    {
      path: '/promotions',
      name: 'PromotionsPage',
      component: SearchPage,
      loadData: (params, search, config) =>
        pageDataLoadingAPI.SearchPage.loadData(params, withDealsQuery(search), config),
    },
    // NOTE: when the private marketplace feature is enabled, the '/products' route is disallowed by the robots.txt resource.
    // If you add new routes that start with '/products*' (e.g. /support), you should add them to the robotsPrivateMarketplace.txt file.
    {
      path: '/products',
      name: 'SearchPage',
      ...authForPrivateMarketplace,
      // Redirect ?deals=true to /promotions; otherwise render SearchPage normally
      component: props => {
        const search = props.location?.search || '';
        if (isDealsQueryActive(search)) {
          return <NamedRedirect name="PromotionsPage" search={withoutDealsQuery(search)} />;
        }
        return <SearchPage {...props} />;
      },
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/products/deals',
      name: 'DealsPageRedirect',
      component: () => <NamedRedirect name="PromotionsPage" />,
    },
    {
      path: '/products/:slug',
      name: 'SearchPageWithSlug',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/comparateur',
      name: 'ComparePageLanding',
      component: ComparePage,
      loadData: pageDataLoadingAPI.ComparePage.loadData,
    },
    {
      path: '/comparateur/:device_type',
      name: 'ComparePage',
      component: ComparePage,
      loadData: pageDataLoadingAPI.ComparePage.loadData,
    },
    // Backward compatibility redirects from legacy '/s' paths
    {
      path: '/s',
      name: 'SearchPageRedirect',
      component: props => <NamedRedirect name="SearchPage" search={props.location?.search} />,
    },
    {
      path: '/s/:listingType',
      name: 'SearchPageWithSlugRedirect',
      component: props => (
        <NamedRedirect
          name="SearchPageWithSlug"
          params={{ slug: props.params?.listingType }}
          search={props.location?.search}
        />
      ),
    },
    {
      path: '/product',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/product/:slug/:id',
      name: 'ListingPage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/product/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: false,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/order-tracking/:token',
      name: 'GuestOrderPage',
      auth: false,
      component: GuestOrderPage,
    },
    {
      path: '/product/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/product/new',
      name: 'NewListingPage',
      auth: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'information' }}
        />
      ),
    },
    {
      path: '/product/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/product/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/product/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/product/:id',
      name: 'ListingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    // Backward compatibility redirects from legacy '/l' paths
    {
      path: '/l',
      name: 'ListingBasePageRedirect',
      component: () => <NamedRedirect name="ListingBasePage" />,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPageRedirect',
      component: props => (
        <NamedRedirect name="ListingPage" params={{ slug: props.params?.slug, id: props.params?.id }} />
      ),
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPageRedirect',
      component: props => (
        <NamedRedirect name="CheckoutPage" params={{ slug: props.params?.slug, id: props.params?.id }} />
      ),
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariantRedirect',
      component: props => (
        <NamedRedirect
          name="ListingPageVariant"
          params={{ slug: props.params?.slug, id: props.params?.id, variant: props.params?.variant }}
        />
      ),
    },
    {
      path: '/l/new',
      name: 'NewListingPageRedirect',
      component: () => <NamedRedirect name="NewListingPage" />,
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPageRedirect',
      component: props => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: props.params?.slug, id: props.params?.id, type: props.params?.type, tab: props.params?.tab }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPageRedirect',
      component: props => (
        <NamedRedirect
          name="EditListingStripeOnboardingPage"
          params={{
            slug: props.params?.slug,
            id: props.params?.id,
            type: props.params?.type,
            tab: props.params?.tab,
            returnURLType: props.params?.returnURLType,
          }}
        />
      ),
    },
    {
      path: '/l/:id',
      name: 'ListingPageCanonicalRedirect',
      component: props => <NamedRedirect name="ListingPageCanonical" params={{ id: props.params?.id }} />,
    },
    // Routes /u/:id désactivées
    // {
    //   path: '/u',
    //   name: 'ProfileBasePage',
    //   component: RedirectToLandingPage,
    // },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      ...authForPrivateMarketplace,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/u/:id/:variant',
      name: 'ProfilePageVariant',
      auth: true,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile',
      name: 'ProfileDashboardPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileDashboardPage,
    },
    {
      path: '/orders',
      name: 'OrdersPage',
      auth: true,
      authPage: 'LoginPage',
      component: OrdersPage,
      loadData: pageDataLoadingAPI.OrdersPage.loadData,
    },
    {
      path: '/mes-demandes',
      name: 'MyRequestsPage',
      auth: true,
      authPage: 'LoginPage',
      component: MyRequestsPage,
    },
    {
      path: '/mes-demandes/nouvelle',
      name: 'NewSupportRequestPage',
      auth: true,
      authPage: 'LoginPage',
      component: NewSupportRequestPage,
    },
    {
      path: '/mes-demandes/:id',
      name: 'SupportRequestDetailPage',
      auth: true,
      authPage: 'LoginPage',
      component: SupportRequestDetailPage,
    },
    {
      path: '/cagnotte-fidelite',
      name: 'CagnotteFidelitePage',
      auth: true,
      authPage: 'LoginPage',
      component: CagnotteFidelitePage,
      loadData: pageDataLoadingAPI.CagnotteFidelitePage.loadData,
    },
    // Hidden: PaymentIntentsPage route
    // {
    //   path: '/payments',
    //   name: 'PaymentIntentsPage',
    //   auth: true,
    //   authPage: 'LoginPage',
    //   component: PaymentIntentsPage,
    // },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },
    // Root-level account settings routes
    { path: '/account-settings', name: 'AccountSettingsPage', auth: true, authPage: 'LoginPage', component: () => <NamedRedirect name="ContactDetailsPage" /> },
    { path: '/contact-details', name: 'ContactDetailsPage', auth: true, authPage: 'LoginPage', component: ContactDetailsPage, loadData: pageDataLoadingAPI.ContactDetailsPage.loadData },
    { path: '/security', name: 'PasswordChangePage', auth: true, authPage: 'LoginPage', component: PasswordChangePage },
    { path: '/payout', name: 'StripePayoutPage', auth: true, authPage: 'LoginPage', component: StripePayoutPage, loadData: pageDataLoadingAPI.StripePayoutPage.loadData },
    { path: '/payments/:returnURLType', name: 'StripePayoutOnboardingPage', auth: true, authPage: 'LoginPage', component: StripePayoutPage, loadData: pageDataLoadingAPI.StripePayoutPage.loadData },
    { path: '/payment-methods', name: 'PaymentMethodsPage', auth: true, authPage: 'LoginPage', component: PaymentMethodsPage, loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData },


    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/signup-provider',
      name: 'SignupProviderPage',
      component: AuthenticationPageProvider,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'orders' }} />,
    },
    {
      path: '/inbox/sales',
      name: 'InboxSalesRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="SalesPage" />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'provider' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/catalogue',
      name: 'CataloguePage',
      auth: true,
      authPage: 'LoginPage',
      component: CataloguePage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    // Legacy redirects from /account/* to new root paths
    { path: '/account', name: 'AccountSettingsBasePage', auth: true, authPage: 'LoginPage', component: () => <NamedRedirect name="ProfileSettingsPage" /> },
    // Redirect old account URLs to new tabbed interface
    { path: '/account/contact-details', name: 'ContactDetailsLegacy', auth: true, authPage: 'LoginPage', component: () => <NamedRedirect name="ProfileSettingsPage" /> },
    { path: '/account/change-password', name: 'PasswordChangeLegacy', auth: true, authPage: 'LoginPage', component: () => <NamedRedirect name="PasswordChangePage" /> },
    { path: '/account/payments', name: 'StripePayoutLegacy', auth: true, authPage: 'LoginPage', component: () => <NamedRedirect name="StripePayoutPage" /> },
    { path: '/account/payments/:returnURLType', name: 'StripePayoutOnboardingLegacy', auth: true, authPage: 'LoginPage', component: props => <NamedRedirect name="StripePayoutOnboardingPage" params={{ returnURLType: props.params?.returnURLType }} /> },
    { path: '/account/payment-methods', name: 'PaymentMethodsLegacy', auth: true, authPage: 'LoginPage', component: () => <NamedRedirect name="PaymentMethodsPage" /> },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePageLegal,
    },
    {
      path: '/legal-notice',
      name: 'LegalNoticePage',
      component: LegalNoticePageLegal,
    },
    // Alias for Terms of Service at /termsofservice (static US-law version)
    {
      path: '/termsofservice',
      name: 'TermsOfServicePageAlias',
      component: TermsOfServiceFallbackPage,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPageLegal,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/no-:missingAccessRight',
      name: 'NoAccessPage',
      component: NoAccessPage,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },

    {
      path: '/articles',
      name: 'ArticlesPage',
      component: ArticlesPage,
    },
      {
        path: '/articles/wi-fi',
        name: 'WiFiPage',
        component: WiFiPage,
      },
      {
        path: '/articles/smart-home-devices',
        name: 'SmartHomeDevicesArticleRedirect',
        component: () => (
          <NamedRedirect
            name="BlogPostPage"
            params={{
              slug: 'reduire-sa-facture-de-chauffage-grace-aux-tetes-thermostatiques-connectees',
            }}
          />
        ),
      },
      {
        path: '/articles/smart-home-protocols',
        name: 'SmartHomeProtocolsPage',
        component: SmartHomeProtocolsPage,
      },
      {
        path: '/articles/video-doorbell',
        name: 'VideoDoorbellPage',
        component: VideoDoorbellPage,
      },
      {
        path: '/articles/robot-vacuum',
        name: 'RobotVacuumArticleRedirect',
        component: () => (
          <NamedRedirect
            name="BlogPostPage"
            params={{
              slug:
                'chauffer-une-chambre-de-bebe-quelle-temperature-et-quelles-solutions-pour-un-confort-optimal',
            }}
          />
        ),
      },
      {
        path: '/articles/smart-lock',
        name: 'SmartLockPage',
        component: SmartLockPage,
      },
      {
        path: '/articles/reduire-sa-facture-de-chauffage-grace-aux-tetes-thermostatiques',
        name: 'ThermostaticHeadsPage',
        component: ThermostaticHeadsPage,
      },
      {
        path: '/articles/5-appareils-connectes-pour-faire-des-economies-de-chauffage',
        name: 'SmartHeatingDevicesPage',
        component: SmartHeatingDevicesPage,
      },
      {
        path: '/articles/chauffer-chambre-bebe-temperature-solutions-confort-optimal',
        name: 'BabyRoomThermostatPage',
        component: BabyRoomThermostatPage,
      },
      {
        path: '/programme-fidelite',
        name: 'ProgrammeFidelitePage',
        component: ProgrammeFidelitePage,
      },
      {
        path: '/programme-affiliation',
        name: 'ProgrammeAffiliationPage',
        component: ProgrammeAffiliationPage,
      },
      // Route dynamique pour les pages à la racine (doit être placée après toutes les routes statiques)
      {
        // Match nested paths (e.g. /camera/camera-wifi)
        path: '/:urlPath+',
        name: 'CustomPage',
        component: BlogPostPage,
        loadData: pageDataLoadingAPI.BlogPostPage.loadData,
        exact: true,
      },
    ];
  };

export default routeConfiguration;
